import { useInfiniteQuery } from '@tanstack/react-query';
import { getLabourItemEventsTimeline } from '../api/sitesApi';
import { useAuthStore } from '../../auth/stores/authStore';
import type { LabourItemEventsTimelineResponse } from '../types/labourItemTimeline';

export const labourItemTimelineKeys = {
  all: ['labourItemTimeline'] as const,
  lists: () => [...labourItemTimelineKeys.all, 'list'] as const,
  list: (
    siteId: number,
    plotId: number,
    labourItemId: number,
    isAuthenticated: boolean
  ) =>
    [
      ...labourItemTimelineKeys.lists(),
      { siteId, plotId, labourItemId, isAuthenticated },
    ] as const,
};

const ITEMS_PER_PAGE = 20;

export interface UseLabourItemEventsTimelineReturn {
  events: LabourItemEventsTimelineResponse['items'];
  totalCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
  loadMore: () => void;
}

/**
 * Hook to fetch labour item events timeline with infinite scroll pagination
 */
export function useLabourItemEventsTimeline(
  siteId: number | null,
  plotId: number | null,
  labourItemId: number | null
): UseLabourItemEventsTimelineReturn {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: labourItemTimelineKeys.list(
      siteId ?? 0,
      plotId ?? 0,
      labourItemId ?? 0,
      isAuthenticated
    ),
    queryFn: async ({ pageParam = 0 }) => {
      if (!siteId || !plotId || !labourItemId) {
        throw new Error('siteId, plotId, and labourItemId are required');
      }

      return await getLabourItemEventsTimeline(siteId, plotId, labourItemId, {
        skip: pageParam,
        take: ITEMS_PER_PAGE,
      });
    },
    getNextPageParam: lastPage => {
      return lastPage?.hasMore ? lastPage.skip + lastPage.take : undefined;
    },
    initialPageParam: 0,
    enabled:
      isAuthenticated &&
      siteId !== null &&
      siteId > 0 &&
      plotId !== null &&
      plotId > 0 &&
      labourItemId !== null &&
      labourItemId > 0,
    staleTime: 2 * 60 * 1000,
  });

  const events = data?.pages.flatMap(page => page?.items ?? []) ?? [];
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  return {
    events,
    totalCount,
    hasMore: hasNextPage ?? false,
    isLoading,
    isLoadingMore: isFetchingNextPage,
    error: error as Error | null,
    loadMore: fetchNextPage,
  };
}
